import React from 'react';
import { AlertTriangle, TrendingUp, Users, Activity } from 'lucide-react';
import { Campaign } from '../../types/campaign';
import { formatCurrency } from '../../utils/formatters';

interface CampaignMetricsProps {
  campaigns: Campaign[];
}

export default function CampaignMetrics({ campaigns }: CampaignMetricsProps) {
  const metrics = {
    totalValue: campaigns.reduce((sum, c) => sum + c.budget, 0),
    totalTargets: campaigns.reduce((sum, c) => sum + (c.metrics?.totalTargets || 0), 0),
    totalContacted: campaigns.reduce((sum, c) => sum + (c.metrics?.contacted || 0), 0),
    totalConverted: campaigns.reduce((sum, c) => sum + (c.metrics?.converted || 0), 0)
  };

  // Calculate rates safely to avoid NaN
  const avgConversionRate = metrics.totalContacted > 0 
    ? (metrics.totalConverted / metrics.totalContacted) * 100 
    : 0;

  const avgResponseRate = metrics.totalTargets > 0
    ? (metrics.totalContacted / metrics.totalTargets) * 100
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Active Campaigns</h3>
          <Activity className="w-5 h-5 text-blue-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{campaigns.filter(c => c.status === 'active').length}</p>
        <p className="mt-1 text-sm text-gray-500">Out of {campaigns.length} total</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Total Targets</h3>
          <Users className="w-5 h-5 text-purple-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{metrics.totalTargets}</p>
        <p className="mt-1 text-sm text-gray-500">Across all campaigns</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Response Rate</h3>
          <TrendingUp className="w-5 h-5 text-green-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{avgResponseRate.toFixed(1)}%</p>
        <p className="mt-1 text-sm text-gray-500">{metrics.totalContacted} responses</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Conversion Rate</h3>
          <AlertTriangle className="w-5 h-5 text-orange-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{avgConversionRate.toFixed(1)}%</p>
        <p className="mt-1 text-sm text-gray-500">{metrics.totalConverted} conversions</p>
      </div>
    </div>
  );
}